// Simple auth state helper using localStorage/sessionStorage.
// Login expires automatically after a fixed TTL (8 hours by default).

const IS_LOGGED_IN_KEY = 'isLoggedIn';
const LOGIN_USER_KEY = 'loginUser';
const EXPIRES_AT_KEY = 'expiresAt';
const AUTH_TOKEN_KEY = 'authToken';
const MUST_RESET_KEY = 'mustResetPassword';
const HEADER_PIC_KEY = 'headerPic';
const LOGIN_EMAIL_KEY = 'loginEmail';
const IS_ADMIN_KEY = 'isAdmin';

export const DEFAULT_LOGIN_TTL_MS = 8 * 60 * 60 * 1000; // 8 hours

function parseExpiresAt(raw) {
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : null;
}

function clearStorage(storage) {
  storage.removeItem(IS_LOGGED_IN_KEY);
  storage.removeItem(LOGIN_USER_KEY);
  storage.removeItem(EXPIRES_AT_KEY);
  storage.removeItem(AUTH_TOKEN_KEY);
  storage.removeItem(MUST_RESET_KEY);
  storage.removeItem(HEADER_PIC_KEY);
  storage.removeItem(LOGIN_EMAIL_KEY);
  storage.removeItem(IS_ADMIN_KEY);
}

export function clearAllLogin() {
  try {
    clearStorage(window.localStorage);
    clearStorage(window.sessionStorage);
  } catch (e) {
    // ignore storage errors
  }
}

export function setLogin({
  username,
  email,
  isAdmin,
  remember,
  ttlMs = DEFAULT_LOGIN_TTL_MS,
  token,
  expiresAt: serverExpiresAt,
  mustResetPassword: resetFlag,
  headerPic
} = {}) {
  const expiresAt =
    serverExpiresAt != null && Number.isFinite(Number(serverExpiresAt))
      ? Number(serverExpiresAt)
      : Date.now() + ttlMs;

  // Ensure only one storage keeps a valid session at a time.
  clearAllLogin();

  const target = remember ? window.localStorage : window.sessionStorage;
  target.setItem(IS_LOGGED_IN_KEY, 'true');
  target.setItem(EXPIRES_AT_KEY, String(expiresAt));
  if (username) target.setItem(LOGIN_USER_KEY, String(username));
  if (email) target.setItem(LOGIN_EMAIL_KEY, String(email));
  target.setItem(IS_ADMIN_KEY, isAdmin ? 'true' : 'false');
  if (token) target.setItem(AUTH_TOKEN_KEY, String(token));
  target.setItem(MUST_RESET_KEY, resetFlag ? 'true' : 'false');
  if (headerPic) {
    target.setItem(HEADER_PIC_KEY, String(headerPic));
  } else {
    target.removeItem(HEADER_PIC_KEY);
  }

  return expiresAt;
}

export function getAuthToken() {
  try {
    const local = window.localStorage.getItem(AUTH_TOKEN_KEY);
    if (local) return local;
    return window.sessionStorage.getItem(AUTH_TOKEN_KEY) || '';
  } catch (e) {
    return '';
  }
}

export function getLoginUser() {
  try {
    const local = window.localStorage.getItem(LOGIN_USER_KEY);
    if (local) return local;
    return window.sessionStorage.getItem(LOGIN_USER_KEY) || '';
  } catch (e) {
    return '';
  }
}

export function getLoginEmail() {
  try {
    const local = window.localStorage.getItem(LOGIN_EMAIL_KEY);
    if (local) return local;
    return window.sessionStorage.getItem(LOGIN_EMAIL_KEY) || '';
  } catch (e) {
    return '';
  }
}

export function getIsAdmin() {
  try {
    if (window.localStorage.getItem(IS_ADMIN_KEY) === 'true') return true;
    if (window.sessionStorage.getItem(IS_ADMIN_KEY) === 'true') return true;
    return false;
  } catch (e) {
    return false;
  }
}

export function getHeaderPic() {
  try {
    const local = window.localStorage.getItem(HEADER_PIC_KEY);
    if (local) return local;
    return window.sessionStorage.getItem(HEADER_PIC_KEY) || '';
  } catch (e) {
    return '';
  }
}

export function setMustResetPassword(value) {
  try {
    const flag = value ? 'true' : 'false';
    if (window.localStorage.getItem(IS_LOGGED_IN_KEY) === 'true') {
      window.localStorage.setItem(MUST_RESET_KEY, flag);
    }
    if (window.sessionStorage.getItem(IS_LOGGED_IN_KEY) === 'true') {
      window.sessionStorage.setItem(MUST_RESET_KEY, flag);
    }
    // Prefer writing to whichever storage currently holds the session
    if (
      window.localStorage.getItem(IS_LOGGED_IN_KEY) !== 'true' &&
      window.sessionStorage.getItem(IS_LOGGED_IN_KEY) !== 'true'
    ) {
      window.sessionStorage.setItem(MUST_RESET_KEY, flag);
    }
  } catch (e) {
    // ignore
  }
}

export function mustResetPassword() {
  try {
    if (window.localStorage.getItem(MUST_RESET_KEY) === 'true') return true;
    if (window.sessionStorage.getItem(MUST_RESET_KEY) === 'true') return true;
    return false;
  } catch (e) {
    return false;
  }
}

export function isLoggedIn() {
  try {
    const now = Date.now();

    const localIsLoggedIn = window.localStorage.getItem(IS_LOGGED_IN_KEY) === 'true';
    const localExpiresAt = parseExpiresAt(window.localStorage.getItem(EXPIRES_AT_KEY));
    if (localIsLoggedIn) {
      if (localExpiresAt && now < localExpiresAt) return true;
      // invalid or expired
      clearStorage(window.localStorage);
    }

    const sessionIsLoggedIn = window.sessionStorage.getItem(IS_LOGGED_IN_KEY) === 'true';
    const sessionExpiresAt = parseExpiresAt(window.sessionStorage.getItem(EXPIRES_AT_KEY));
    if (sessionIsLoggedIn) {
      if (sessionExpiresAt && now < sessionExpiresAt) return true;
      clearStorage(window.sessionStorage);
    }

    return false;
  } catch (e) {
    return false;
  }
}



// WEBPACK FOOTER //
// ./src/utils/auth.js